import { Avatar, ButtonBase, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { mdiEmailOutline, mdiMessageTextOutline } from '@mdi/js';
import Icon from '@mdi/react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import SideBadge from 'components/SideBadge/SideBadge';

// Chat version of the shared ProjectListItem pill
// (src/scenes/Projects/components/ProjectListItem.js) — same container,
// title button and icon-slot styles, but with the chat drawer's actions.
const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    ...theme.component.projectsListButton,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
  },
  firstButton: {
    padding: theme.spacing(1),
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    transition: 'background-color 0.3s',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    textDecorationLine: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  buttonTitle: {
    ...theme.typography.fontPanton,
    fontSize: '0.9rem',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    // overflowY must stay visible so the inline avatar isn't clipped
    // against the line box (same combination as ProjectListItem).
    overflowX: 'clip',
    overflowY: 'visible',
    textOverflow: 'ellipsis',
    display: 'inline-block',
  },
  buttonSubtitle: {
    fontSize: '0.75rem',
    lineHeight: '1',
    maxWidth: '100%',
    overflowY: 'visible',
    overflowX: 'clip',
  },
  slotButton: {
    padding: theme.spacing(0.5),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  titleIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
  },
  titleAvatar: {
    width: 20,
    height: 20,
    fontSize: '0.65rem',
  },
}));

const ChatProjectListItem = React.memo(
  ({
    title,
    subtitle,
    lastMessageAt,
    projectKey,
    thread,
    avatarSrc,
    showAvatar = false,
    showEmailButton = false,
    showChatButton = true,
    unreadEmailCount = 0,
    unreadChatCount = 0,
    onEmailButtonClick,
    onChatClick,
  }) => {
    const classes = useStyles();

    const formattedTime = lastMessageAt
      ? moment(lastMessageAt).format('DD.MM.YYYY HH:mm')
      : null;

    const handleChatClick = React.useCallback(() => {
      if (onChatClick) {
        onChatClick(thread);
      }
    }, [onChatClick, thread]);

    const buttonCount = (showEmailButton ? 1 : 0) + (showChatButton ? 1 : 0);
    const titleXs = 12 - buttonCount * 2;

    // The title acts as the primary action: open the chat thread, or for
    // mailbox rows (email-only) navigate to the project email view.
    const titleButtonProps = showChatButton
      ? { onClick: handleChatClick }
      : {
          component: RouterLink,
          to: `/project/${projectKey}/email`,
          onClick: onEmailButtonClick,
        };

    return (
      <div className={classes.root}>
        <Grid container direction="row" className={classes.buttonContainer}>
          <Grid item xs={titleXs}>
            <ButtonBase className={classes.firstButton} {...titleButtonProps}>
              <Typography className={classes.buttonTitle}>
                {showAvatar && (
                  <span className={classes.titleIcon}>
                    <Avatar
                      component="span"
                      src={avatarSrc}
                      className={classes.titleAvatar}
                    >
                      {title ? title.charAt(0).toUpperCase() : ''}
                    </Avatar>
                  </span>
                )}
                {title || ''}
              </Typography>
              <Typography className={classes.buttonSubtitle} noWrap>
                {subtitle || '\u00A0'}
              </Typography>
              <Typography className={classes.buttonSubtitle} noWrap>
                {formattedTime || '\u00A0'}
              </Typography>
            </ButtonBase>
          </Grid>
          {showEmailButton && (
            <Grid
              item
              xs={2}
              justifyContent="center"
              alignItems="center"
              container
            >
              <ButtonBase
                className={classes.slotButton}
                component={RouterLink}
                to={`/project/${projectKey}/email`}
                onClick={onEmailButtonClick}
              >
                <SideBadge
                  badgeContent={unreadEmailCount}
                  color="secondary"
                  invisible={(unreadEmailCount || 0) === 0}
                >
                  <Icon path={mdiEmailOutline} size={1} />
                </SideBadge>
              </ButtonBase>
            </Grid>
          )}
          {showChatButton && (
            <Grid
              item
              xs={2}
              justifyContent="center"
              alignItems="center"
              container
            >
              <ButtonBase
                className={classes.slotButton}
                onClick={handleChatClick}
              >
                <SideBadge
                  badgeContent={unreadChatCount}
                  color="secondary"
                  invisible={(unreadChatCount || 0) === 0}
                >
                  <Icon path={mdiMessageTextOutline} size={1} />
                </SideBadge>
              </ButtonBase>
            </Grid>
          )}
        </Grid>
      </div>
    );
  }
);

ChatProjectListItem.displayName = 'ChatProjectListItem';

ChatProjectListItem.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  lastMessageAt: PropTypes.string,
  projectKey: PropTypes.string.isRequired,
  thread: PropTypes.object,
  avatarSrc: PropTypes.string,
  showAvatar: PropTypes.bool,
  showEmailButton: PropTypes.bool,
  showChatButton: PropTypes.bool,
  unreadEmailCount: PropTypes.number,
  unreadChatCount: PropTypes.number,
  onEmailButtonClick: PropTypes.func,
  onChatClick: PropTypes.func,
};

export default ChatProjectListItem;
